import React, { useState, useEffect, useMemo } from "react";
import {
  Clock, AlertCircle, CalendarX, Users, Loader2, Lock, Unlock,
  MousePointerClick, CheckSquare,
} from "lucide-react";
import { cn } from "../../../../lib/cn";

/**
 * PanelFranjasDelDia v3 — Fase 12h.
 *
 * Añade sobre v2 (Fase 12d):
 *   - Botón "Bloquear todo el día" en el header (todos los slots del día,
 *     un solo modal con las N franjas como rangos).
 *   - Modo "Selección múltiple": toggle en el header → click en slots
 *     libres los marca → botón "Bloquear N slots" abre modal bulk.
 *
 * Props nuevas:
 *   onBloquearDia: ({ franjas, fecha }) => void
 *     Se llama al pulsar "Bloquear todo el día". `franjas` es el array
 *     completo de franjas del día (con hora_inicio/hora_fin).
 *   onBloquearSeleccion: ({ slots, fecha }) => void
 *     Se llama con los slots marcados. `slots` es array de
 *     {hora_inicio, hora_fin}.
 *
 * Retrocompatible: si estas props no se pasan, los controles no aparecen
 * y el panel funciona igual que v2.
 */

const DIAS_LABEL = ["", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const MESES_LABEL = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function pad(n) { return String(n).padStart(2, "0"); }
function timeAMin(t) {
  if (!t) return 0;
  const [h, m] = String(t).split(":").map((n) => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
}
function minATime(min) {
  return `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
}
function fechaADiaISO(yyyymmdd) {
  if (!yyyymmdd) return null;
  const d = new Date(`${yyyymmdd}T12:00:00`);
  const js = d.getDay();
  return js === 0 ? 7 : js;
}
function formatearFecha(yyyymmdd) {
  if (!yyyymmdd) return "";
  const d = new Date(`${yyyymmdd}T12:00:00`);
  const dia = DIAS_LABEL[fechaADiaISO(yyyymmdd)];
  return `${dia}, ${d.getDate()} de ${MESES_LABEL[d.getMonth()]}`;
}

function keySlot(hora_inicio, hora_fin) {
  return `${hora_inicio}-${hora_fin}`;
}

// ────────────────────────────────────────────────────────────────

export default function PanelFranjasDelDia({
  campanaId,
  fechaSeleccionada,
  duracionCita,
  franjasPorDia,
  citasDelDia,
  bloqueosDelDia = null,
  onBloquearFranja = null,
  onBloquearSlot = null,
  onBloquearDia = null,
  onBloquearSeleccion = null,
  onDesbloquear = null,
  cargarFranjas,
  cargarCitas,
}) {

  const [franjasState, setFranjasState] = useState(franjasPorDia || null);
  const [citasState, setCitasState] = useState(citasDelDia || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estado del modo "selección múltiple"
  const [modoSel, setModoSel] = useState(false);
  const [slotsSel, setSlotsSel] = useState({});  // { key: {hora_inicio, hora_fin} }

  useEffect(() => {
    if (franjasPorDia) { setFranjasState(franjasPorDia); return; }
    if (!campanaId || !cargarFranjas) return;
    let cancelado = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const data = await cargarFranjas(campanaId);
        if (!cancelado) setFranjasState(data);
      } catch (e) {
        if (!cancelado) setError("No se pudieron cargar las franjas.");
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => { cancelado = true; };
  }, [campanaId, franjasPorDia, cargarFranjas]);

  useEffect(() => {
    if (citasDelDia) { setCitasState(citasDelDia); return; }
    if (!campanaId || !fechaSeleccionada || !cargarCitas) return;
    let cancelado = false;
    (async () => {
      try {
        const data = await cargarCitas(campanaId, fechaSeleccionada);
        if (!cancelado) setCitasState(data || []);
      } catch (e) {
        if (!cancelado) setCitasState([]);
      }
    })();
    return () => { cancelado = true; };
  }, [campanaId, fechaSeleccionada, citasDelDia, cargarCitas]);

  // Cambiar de día → salir del modo selección
  useEffect(() => {
    setModoSel(false);
    setSlotsSel({});
  }, [fechaSeleccionada]);

  const diaISO = fechaSeleccionada ? fechaADiaISO(fechaSeleccionada) : null;

  const franjasDelDia = useMemo(() => {
    if (!franjasState || !diaISO) return [];
    const lista = franjasState[diaISO] || franjasState[String(diaISO)] || [];
    return lista.slice().sort((a, b) => timeAMin(a.hora_inicio) - timeAMin(b.hora_inicio));
  }, [franjasState, diaISO]);

  const franjasConSlots = useMemo(() => {
    if (!duracionCita) return franjasDelDia.map((f) => ({ ...f, slots: [] }));
    return franjasDelDia.map((franja) => {
      const ini = timeAMin(franja.hora_inicio);
      const fin = timeAMin(franja.hora_fin);
      const slots = [];
      for (let t = ini; t + duracionCita <= fin; t += duracionCita) {
        const horaIni = minATime(t);
        const horaFin = minATime(t + duracionCita);
        const ocupadasAqui = citasState.filter((c) => {
          const cIni = timeAMin(c.hora_inicio);
          const cFin = timeAMin(c.hora_fin || (c.hora_inicio &&
            minATime(timeAMin(c.hora_inicio) + duracionCita)));
          return cIni < (t + duracionCita) && cFin > t;
        });
        const bloqueadoPor = (bloqueosDelDia || []).find((b) => {
          const bIni = timeAMin(b.hora_inicio);
          const bFin = timeAMin(b.hora_fin);
          return bIni < (t + duracionCita) && bFin > t;
        });
        slots.push({ hora_inicio: horaIni, hora_fin: horaFin, ocupadas: ocupadasAqui, bloqueadoPor });
      }
      const total = slots.length;
      const ocupados = slots.filter((s) => s.ocupadas.length > 0).length;
      const bloqueados = slots.filter((s) => s.bloqueadoPor).length;
      const libres = total - ocupados - bloqueados;
      const franjaEnteramenteBloqueada = (bloqueosDelDia || []).find((b) =>
        timeAMin(b.hora_inicio) <= ini && timeAMin(b.hora_fin) >= fin
      );
      return { ...franja, slots, total, ocupados, bloqueados, libres, franjaEnteramenteBloqueada };
    });
  }, [franjasDelDia, citasState, bloqueosDelDia, duracionCita]);

  const modoLectura = !onBloquearFranja && !onBloquearSlot &&
                      !onBloquearDia && !onBloquearSeleccion && !onDesbloquear;

  const totalSeleccionados = Object.keys(slotsSel).length;

  // ─── Handlers de selección múltiple ───

  function toggleModoSel() {
    setModoSel(!modoSel);
    setSlotsSel({});
  }

  function toggleSlotSel(slot) {
    const k = keySlot(slot.hora_inicio, slot.hora_fin);
    setSlotsSel((prev) => {
      const nuevo = { ...prev };
      if (nuevo[k]) delete nuevo[k];
      else nuevo[k] = { hora_inicio: slot.hora_inicio, hora_fin: slot.hora_fin };
      return nuevo;
    });
  }

  function confirmarSeleccion() {
    if (totalSeleccionados === 0 || !onBloquearSeleccion) return;
    const slots = Object.values(slotsSel);
    onBloquearSeleccion({ slots, fecha: fechaSeleccionada });
  }

  function cancelarSeleccion() {
    setModoSel(false);
    setSlotsSel({});
  }

  function handleBloquearDia() {
    if (!onBloquearDia || franjasDelDia.length === 0) return;
    onBloquearDia({
      franjas: franjasDelDia.map((f) => ({
        hora_inicio: f.hora_inicio,
        hora_fin: f.hora_fin,
      })),
      fecha: fechaSeleccionada,
    });
  }

  // ────────────────────────────────────────────────────────────
  //  Render
  // ────────────────────────────────────────────────────────────

  return (
    <aside className="bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
        {fechaSeleccionada ? (
          <>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">
                  Día seleccionado
                </div>
                <div className="text-sm font-semibold text-slate-900 mt-0.5">
                  {formatearFecha(fechaSeleccionada)}
                </div>
              </div>

              {/* Acciones a nivel día */}
              {!modoLectura && franjasDelDia.length > 0 && (
                <div className="flex items-center gap-1 shrink-0">
                  {onBloquearDia && (
                    <button
                      type="button"
                      onClick={handleBloquearDia}
                      className="text-[10px] text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2 py-1 rounded-md transition-colors flex items-center gap-1 whitespace-nowrap"
                      title="Bloquear todos los slots del día"
                    >
                      <Lock className="w-3 h-3" />
                      Bloquear día
                    </button>
                  )}
                  {onBloquearSeleccion && (
                    <button
                      type="button"
                      onClick={toggleModoSel}
                      className={cn(
                        "text-[10px] px-2 py-1 rounded-md transition-colors flex items-center gap-1 whitespace-nowrap",
                        modoSel
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                      )}
                      title="Modo selección múltiple"
                    >
                      <MousePointerClick className="w-3 h-3" />
                      Sel. múltiple
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Barra de acción del modo selección */}
            {modoSel && (
              <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between gap-2">
                <div className="text-[11px] text-slate-600 flex items-center gap-1.5">
                  <CheckSquare className="w-3 h-3" />
                  <span className="tabular-nums">
                    {totalSeleccionados} {totalSeleccionados === 1 ? "slot marcado" : "slots marcados"}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={cancelarSeleccion}
                    className="text-[10px] text-slate-500 hover:text-slate-900 px-2 py-1"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={confirmarSeleccion}
                    disabled={totalSeleccionados === 0}
                    className={cn(
                      "text-[10px] px-2.5 py-1 rounded-md font-medium transition-colors flex items-center gap-1",
                      totalSeleccionados === 0
                        ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                        : "bg-primary text-white hover:bg-primary/90"
                    )}
                  >
                    <Lock className="w-3 h-3" />
                    Bloquear {totalSeleccionados}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-slate-500">
            Selecciona un día del calendario
          </div>
        )}
      </div>

      <div className="flex-1 p-4 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            <span className="text-xs">Cargando franjas...</span>
          </div>
        )}

        {error && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {!loading && !error && fechaSeleccionada && franjasDelDia.length === 0 && (
          <EmptyState>Sin franjas configuradas para este día.</EmptyState>
        )}

        {!loading && !error && !fechaSeleccionada && (
          <EmptyState muted>
            Selecciona un día del calendario para ver las franjas y los slots disponibles.
          </EmptyState>
        )}

        {!loading && franjasConSlots.length > 0 && (
          <div className="space-y-3">
            {franjasConSlots.map((franja, idx) => (
              <FranjaCard
                key={franja.id || idx}
                franja={franja}
                fechaSeleccionada={fechaSeleccionada}
                modoLectura={modoLectura}
                modoSel={modoSel}
                slotsSel={slotsSel}
                onBloquearFranja={onBloquearFranja}
                onBloquearSlot={onBloquearSlot}
                onDesbloquear={onDesbloquear}
                onToggleSlotSel={toggleSlotSel}
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}

// ────────────────────────────────────────────────────────────────

function FranjaCard({
  franja, fechaSeleccionada, modoLectura, modoSel, slotsSel,
  onBloquearFranja, onBloquearSlot, onDesbloquear, onToggleSlotSel,
}) {
  const {
    slots = [], total = 0, ocupados = 0, bloqueados = 0, libres = 0,
    franjaEnteramenteBloqueada,
  } = franja;

  const pctOcupados = total > 0 ? (ocupados / total) * 100 : 0;

  return (
    <div className={cn(
      "border rounded-lg overflow-hidden bg-white",
      franjaEnteramenteBloqueada
        ? "border-slate-300 bg-slate-50"
        : "border-slate-200"
    )}>
      <div className="px-3.5 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {franjaEnteramenteBloqueada
            ? <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            : <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
          <span className={cn(
            "text-sm font-medium tabular-nums",
            franjaEnteramenteBloqueada ? "text-slate-600 line-through" : "text-slate-900"
          )}>
            {String(franja.hora_inicio).slice(0, 5)} →{" "}
            {String(franja.hora_fin).slice(0, 5)}
          </span>
        </div>

        {!modoLectura && !modoSel && (
          <div className="flex items-center gap-1">
            {franjaEnteramenteBloqueada ? (
              onDesbloquear && (
                <button
                  type="button"
                  onClick={() => onDesbloquear(franjaEnteramenteBloqueada)}
                  className="text-[11px] text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded-md transition-colors flex items-center gap-1"
                  title="Desbloquear franja"
                >
                  <Unlock className="w-3 h-3" />
                  Desbloquear
                </button>
              )
            ) : (
              onBloquearFranja && (
                <button
                  type="button"
                  onClick={() => onBloquearFranja({ franja, fecha: fechaSeleccionada })}
                  className="text-[11px] text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2 py-1 rounded-md transition-colors flex items-center gap-1"
                  title="Bloquear franja entera"
                >
                  <Lock className="w-3 h-3" />
                  Bloquear franja
                </button>
              )
            )}
          </div>
        )}
      </div>

      <div className="px-3.5 py-2.5">
        <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Users className="w-3 h-3" />
            <span className="tabular-nums">
              <strong className="text-slate-900">{ocupados}</strong>/{total} ocupados
            </span>
          </span>
          {bloqueados > 0 && (
            <>
              <span className="text-slate-300">·</span>
              <span className="inline-flex items-center gap-1 text-slate-500">
                <Lock className="w-3 h-3" />
                <span className="tabular-nums">{bloqueados} bloq.</span>
              </span>
            </>
          )}
          <span className="text-slate-300">·</span>
          <span className="tabular-nums text-emerald-700">{libres} libres</span>
        </div>

        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden flex">
          <div
            className={cn(
              "h-full transition-all",
              pctOcupados >= 100 ? "bg-red-500" :
              pctOcupados >= 70 ? "bg-amber-500" :
              "bg-emerald-500"
            )}
            style={{ width: `${pctOcupados}%` }}
          />
          {total > 0 && (
            <div
              className="h-full bg-slate-400"
              style={{ width: `${(bloqueados / total) * 100}%` }}
            />
          )}
        </div>

        {slots.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-1">
            {slots.map((s, i) => (
              <SlotChip
                key={i}
                slot={s}
                fechaSeleccionada={fechaSeleccionada}
                franja={franja}
                modoLectura={modoLectura}
                modoSel={modoSel}
                seleccionado={!!slotsSel[keySlot(s.hora_inicio, s.hora_fin)]}
                onBloquearSlot={onBloquearSlot}
                onDesbloquear={onDesbloquear}
                onToggleSlotSel={onToggleSlotSel}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────

function SlotChip({
  slot, fechaSeleccionada, franja, modoLectura, modoSel, seleccionado,
  onBloquearSlot, onDesbloquear, onToggleSlotSel,
}) {
  const ocupado = slot.ocupadas.length > 0;
  const bloqueado = !!slot.bloqueadoPor;

  let tooltip = "Disponible";
  if (bloqueado) {
    tooltip = `Bloqueado${slot.bloqueadoPor.motivo ? `: ${slot.bloqueadoPor.motivo}` : ""}`;
  } else if (ocupado) {
    tooltip = `Ocupado por: ${slot.ocupadas.map((o) => o.persona_nombre || o.usuario?.nombre_completo || "Cita").join(", ")}`;
  }

  // En modo selección múltiple: solo slots libres son clickables (para marcar)
  // En modo normal: comportamiento de 12d
  const clickableSel = modoSel && !ocupado && !bloqueado;
  const clickableNormal = !modoSel && !modoLectura && !ocupado && (
    (bloqueado && !!onDesbloquear) || (!bloqueado && !!onBloquearSlot)
  );
  const clickable = clickableSel || clickableNormal;

  function handleClick() {
    if (!clickable) return;
    if (modoSel) {
      onToggleSlotSel(slot);
    } else if (bloqueado) {
      onDesbloquear?.(slot.bloqueadoPor);
    } else {
      onBloquearSlot?.({ franja, slot, fecha: fechaSeleccionada });
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!clickable}
      title={tooltip + (clickable && !modoSel
        ? (bloqueado ? " · Click para desbloquear" : " · Click para bloquear")
        : "")}
      className={cn(
        "text-[10px] px-1.5 py-0.5 rounded text-center tabular-nums transition-colors border",
        // Ocupado
        ocupado && "bg-red-50 text-red-700 line-through cursor-not-allowed border-transparent",
        // Bloqueado (sin ocupar)
        bloqueado && !ocupado && "bg-slate-200 text-slate-600 line-through border-transparent",
        bloqueado && !ocupado && clickable && !modoSel && "hover:bg-emerald-100 hover:text-emerald-700",
        // Libre no seleccionado
        !ocupado && !bloqueado && !seleccionado && "bg-emerald-50 text-emerald-700 border-transparent",
        !ocupado && !bloqueado && !seleccionado && clickableNormal &&
          "hover:bg-slate-100 hover:text-slate-900 cursor-pointer",
        // Libre en modo selección: outline
        clickableSel && !seleccionado && "hover:border-primary hover:bg-primary/5 cursor-pointer",
        // Seleccionado
        seleccionado && "bg-primary/15 text-primary font-semibold border-primary",
      )}
    >
      {slot.hora_inicio}
    </button>
  );
}

function EmptyState({ children, muted = false }) {
  return (
    <div className={cn(
      "flex flex-col items-center justify-center text-center py-10 px-4",
      muted ? "text-slate-400" : "text-slate-500"
    )}>
      <CalendarX className="w-8 h-8 mb-3 text-slate-300" />
      <p className="text-xs leading-relaxed max-w-[220px]">{children}</p>
    </div>
  );
}
